import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, FileText, Download, Trash2, Edit, Settings, Eye, FolderUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    brouillon: { label: 'Brouillon', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    signe: { label: 'Signe', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    annule: { label: 'Annule', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
    importe: { label: 'Importé', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
};

const DocumentsPage = () => {
    const { token, isChefDispositif, isCommune } = useAuth();
    const canEdit = isChefDispositif || isCommune;
    const [documents, setDocuments] = useState([]);
    const [docTypes, setDocTypes] = useState([]);
    const [communeName, setCommuneName] = useState('');
    const [loading, setLoading] = useState(true);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [communeInput, setCommuneInput] = useState('');

    // Editor state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorDoc, setEditorDoc] = useState(null); // null = new, object = editing
    const [editorType, setEditorType] = useState('');
    const [editorBody, setEditorBody] = useState('');
    const [editorStatus, setEditorStatus] = useState('brouillon');
    const [editorObjet, setEditorObjet] = useState('');

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [docsRes, typesRes, settingsRes] = await Promise.all([
                axios.get(`${API}/documents`, { headers }),
                axios.get(`${API}/documents/types`, { headers }),
                axios.get(`${API}/settings/commune`, { headers })
            ]);
            setDocuments(docsRes.data);
            setDocTypes(typesRes.data);
            setCommuneName(settingsRes.data.commune_name || '');
            setCommuneInput(settingsRes.data.commune_name || '');
        } catch { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleBulkDocs = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        try {
            const res = await axios.post(`${API}/documents/bulk-upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
            toast.success(`${res.data.created} document(s) importé(s)`);
            if (res.data.errors?.length) res.data.errors.forEach(er => toast.error(`${er.file} : ${er.detail}`));
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur import');
        }
    };

    const getTypeLabel = (id) => docTypes.find(t => t.id === id)?.label || id;

    const buildDefaultBody = (typeId) => {
        const templates = {
            arrete_mise_securite: `Le Maire de ${communeName || '[COMMUNE]'},

Vu le Code General des Collectivites Territoriales, notamment les articles L.2212-2 et L.2212-4,
Vu le Plan Communal de Sauvegarde,

CONSIDERANT que [decrire la situation de danger],
CONSIDERANT que la securite des personnes et des biens est menacee,

ARRETE :

Article 1 : [Mesures de mise en securite ordonnees]

Article 2 : [Zones ou adresses concernees]

Article 3 : Le present arrete est applicable pour une duree de [duree].

Article 4 : Le present arrete sera notifie aux personnes concernees et affiche en mairie.`,

            requisition_moyens: `Le Maire de ${communeName || '[COMMUNE]'},

Vu le Code General des Collectivites Territoriales, notamment l'article L.2212-2,
Vu le Plan Communal de Sauvegarde,

CONSIDERANT [motif de la requisition],

ARRETE :

Article 1 : Est requisitionne(e) l'organisme suivant :
[Nom de l'organisme]

Article 2 : Les moyens suivants sont requis :
[Description des moyens]

Article 3 : Le lieu d'intervention est fixe a :
[Lieu]

Article 4 : La presente requisition est valable pour une duree de [duree].`,

            arrete_evacuation: `Le Maire de ${communeName || '[COMMUNE]'},

Vu le Code General des Collectivites Territoriales,
Vu le Plan Communal de Sauvegarde,

CONSIDERANT [decrire le risque justifiant l'evacuation],
CONSIDERANT que la securite des habitants de la zone concernee ne peut plus etre assuree,

ARRETE :

Article 1 : L'evacuation est ordonnee pour la zone suivante :
[Delimitation de la zone]

Article 2 : Nombre de personnes estimees concernees : [nombre]

Article 3 : Le point de regroupement est fixe a :
[Lieu du point de regroupement]

Article 4 : Les services municipaux et les forces de l'ordre sont charges de l'execution du present arrete.

Article 5 : Le present arrete est applicable immediatement pour une duree de [duree].`,

            main_levee_evacuation: `Le Maire de ${communeName || '[COMMUNE]'},

Vu l'arrete n [reference] du [date] ordonnant l'evacuation de [zone],

CONSIDERANT que [motif de la levee - le danger a cesse / la situation est maitrisee],

ARRETE :

Article 1 : La main levee de l'evacuation est prononcee pour la zone :
[Zone concernee]

Article 2 : Les habitants sont autorises a regagner leur domicile a compter de la notification du present arrete.

Article 3 : Le present arrete sera notifie aux personnes concernees.`,

            requisition_locaux: `Le Maire de ${communeName || '[COMMUNE]'},

Vu le Code General des Collectivites Territoriales,
Vu le Plan Communal de Sauvegarde,

CONSIDERANT [motif de la requisition du local],

ARRETE :

Article 1 : Est requisitionne le local suivant :
Proprietaire : [nom du proprietaire]
Adresse : [adresse du local]

Article 2 : Ce local sera utilise pour :
[Usage prevu]

Article 3 : La presente requisition est valable pour une duree de [duree].

Article 4 : Le proprietaire sera indemnise conformement a la reglementation en vigueur.`,
        };
        return templates[typeId] || `Le Maire de ${communeName || '[COMMUNE]'},\n\n[Contenu du document]`;
    };

    const openNewDoc = () => {
        setEditorDoc(null);
        setEditorType('');
        setEditorBody('');
        setEditorObjet('');
        setEditorStatus('brouillon');
        setEditorOpen(true);
    };

    const openEditDoc = (doc) => {
        setEditorDoc(doc);
        setEditorType(doc.doc_type);
        setEditorBody(doc.fields?.body || '');
        setEditorObjet(doc.fields?.objet || '');
        setEditorStatus(doc.status);
        setEditorOpen(true);
    };

    const handleTypeChange = (typeId) => {
        setEditorType(typeId);
        if (!editorDoc) {
            setEditorBody(buildDefaultBody(typeId));
        }
    };

    const handleSave = async () => {
        if (!editorType) { toast.error('Choisissez un type de document'); return; }
        try {
            const fields = { objet: editorObjet, body: editorBody };
            if (editorDoc) {
                await axios.put(`${API}/documents/${editorDoc.id}`, { fields, status: editorStatus }, { headers });
                toast.success('Document enregistre');
            } else {
                const res = await axios.post(`${API}/documents`, { doc_type: editorType, fields }, { headers });
                toast.success(`Document N ${res.data.number} cree`);
            }
            setEditorOpen(false);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce document ?')) return;
        try { await axios.delete(`${API}/documents/${id}`, { headers }); toast.success('Supprime'); fetchData(); }
        catch { toast.error('Erreur'); }
    };

    const handleExportPDF = (doc) => {
        // Direct link - handled by <a> tags in JSX
    };

    const handleSaveCommune = async () => {
        try {
            await axios.put(`${API}/settings/commune`, { commune_name: communeInput }, { headers });
            setCommuneName(communeInput);
            setSettingsOpen(false);
            toast.success('Nom de commune enregistre');
        } catch { toast.error('Erreur'); }
    };

    const countByStatus = (s) => documents.filter(d => d.status === s).length;
    const now = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

    return (
        <div className="space-y-6" data-testid="documents-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="text-3xl font-bold text-white">Documents communaux</h1>
                    <p className="page-subtitle">{communeName ? `Commune de ${communeName}` : 'Arretes, requisitions et documents officiels'}</p>
                </div>
                <div className="flex items-center gap-2">
                    {isChefDispositif && (
                        <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => setSettingsOpen(true)} data-testid="commune-settings-btn">
                            <Settings className="w-4 h-4" />
                        </Button>
                    )}
                    {canEdit && (
                        <>
                            <Button variant="outline" className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10" onClick={() => document.getElementById('docs-bulk-input').click()} data-testid="docs-bulk-upload-btn">
                                <FolderUp className="w-4 h-4 mr-2" /> Importer un dossier
                            </Button>
                            <input id="docs-bulk-input" type="file" multiple accept=".pdf,.doc,.docx,.odt,.jpg,.png" className="hidden" onChange={(e) => { handleBulkDocs(e.target.files); e.target.value = ''; }} data-testid="docs-bulk-input" />
                            <Button className="tactical-btn" onClick={openNewDoc} data-testid="add-document-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nouveau document
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="stat-card border-l-blue-500"><CardContent className="p-0"><p className="stat-value">{documents.length}</p><p className="stat-label">Total</p></CardContent></Card>
                <Card className="stat-card border-l-amber-500"><CardContent className="p-0"><p className="stat-value">{countByStatus('brouillon')}</p><p className="stat-label">Brouillons</p></CardContent></Card>
                <Card className="stat-card border-l-emerald-500"><CardContent className="p-0"><p className="stat-value">{countByStatus('signe')}</p><p className="stat-label">Signes</p></CardContent></Card>
                <Card className="stat-card border-l-red-500"><CardContent className="p-0"><p className="stat-value">{countByStatus('annule')}</p><p className="stat-label">Annules</p></CardContent></Card>
            </div>

            <Card className="content-card">
                <CardHeader className="card-header"><CardTitle className="card-title flex items-center gap-2"><FileText className="w-5 h-5 text-blue-500" /> Documents</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">N</TableHead>
                                <TableHead className="text-zinc-400">Type</TableHead>
                                <TableHead className="text-zinc-400">Objet</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                <TableHead className="text-zinc-400">Par</TableHead>
                                <TableHead className="text-zinc-400">Date</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {documents.map(doc => {
                                const sc = statusConfig[doc.status] || statusConfig.brouillon;
                                return (
                                    <TableRow key={doc.id} className="border-zinc-800 cursor-pointer hover:bg-zinc-800/40" data-testid={`doc-row-${doc.id}`} onClick={() => canEdit && !doc.storage_path ? openEditDoc(doc) : null}>
                                        <TableCell className="font-mono text-white font-medium">{doc.number}</TableCell>
                                        <TableCell className="text-zinc-300 text-sm">{doc.doc_type_label}</TableCell>
                                        <TableCell className="text-zinc-400 text-sm max-w-[200px] truncate">{doc.fields?.objet || '-'}</TableCell>
                                        <TableCell><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${sc.color}`}>{sc.label}</span></TableCell>
                                        <TableCell className="text-zinc-400 text-sm">{doc.created_by}</TableCell>
                                        <TableCell className="text-zinc-400 font-mono text-xs">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                <a href={doc.storage_path ? `${API}/documents/${doc.id}/file?token=${token}` : `${API}/documents/${doc.id}/pdf?token=${token}`}
                                                    target="_blank" rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-cyan-400 hover:text-cyan-300"
                                                    data-testid={`pdf-${doc.id}`} onClick={(e) => e.stopPropagation()}>
                                                    <Download className="w-4 h-4" />
                                                </a>
                                                {canEdit && (
                                                    <>
                                                        {!doc.storage_path && <Button size="sm" variant="ghost" className="tactical-btn-ghost" onClick={() => openEditDoc(doc)} data-testid={`edit-doc-${doc.id}`}><Edit className="w-4 h-4" /></Button>}
                                                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(doc.id)}><Trash2 className="w-4 h-4" /></Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {documents.length === 0 && (
                                <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-500"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun document</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* ====== DOCUMENT EDITOR MODAL ====== */}
            <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
                <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 max-h-[92vh] overflow-hidden p-0">
                    {/* Toolbar */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900 shrink-0">
                        <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-400" />
                            <span className="text-white font-semibold text-sm">
                                {editorDoc ? `${editorDoc.doc_type_label} N ${editorDoc.number}` : 'Nouveau document'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {!editorDoc && (
                                <Select value={editorType} onValueChange={handleTypeChange}>
                                    <SelectTrigger className="tactical-input w-[220px] h-8 text-xs" data-testid="editor-type-select"><SelectValue placeholder="Type de document" /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        {docTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            )}
                            {editorDoc && (
                                <Select value={editorStatus} onValueChange={setEditorStatus}>
                                    <SelectTrigger className="tactical-input w-[130px] h-8 text-xs" data-testid="editor-status"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        <SelectItem value="brouillon">Brouillon</SelectItem>
                                        <SelectItem value="signe">Signe</SelectItem>
                                        <SelectItem value="annule">Annule</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                            <Button variant="ghost" size="sm" className="text-zinc-400" onClick={() => setEditorOpen(false)}>Annuler</Button>
                            <Button size="sm" className="tactical-btn" onClick={handleSave} disabled={!editorType} data-testid="editor-save">Enregistrer</Button>
                        </div>
                    </div>

                    {/* Document page */}
                    <div className="overflow-y-auto p-6 bg-zinc-950/50" style={{ maxHeight: 'calc(92vh - 56px)' }}>
                        <div className="mx-auto bg-white rounded shadow-2xl" style={{ maxWidth: '700px', minHeight: '800px' }}>
                            {/* Document header */}
                            <div className="px-10 pt-10 pb-4 text-center border-b-2 border-blue-600">
                                <p className="text-[11px] text-gray-500 uppercase tracking-[3px]">Republique Francaise</p>
                                {communeName && <p className="text-sm font-bold text-gray-800 mt-2">COMMUNE DE {communeName.toUpperCase()}</p>}
                                <p className="text-lg font-bold text-gray-900 mt-4">
                                    {editorType ? getTypeLabel(editorType).toUpperCase() : 'SELECTIONNEZ UN TYPE'}
                                </p>
                                {editorDoc && <p className="text-xs text-gray-500 mt-1">N {editorDoc.number}</p>}
                                <p className="text-xs text-gray-500 mt-1">{now}</p>
                            </div>

                            {/* Objet field */}
                            <div className="px-10 pt-5">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xs font-bold text-gray-700 shrink-0">OBJET :</span>
                                    <input
                                        value={editorObjet}
                                        onChange={(e) => setEditorObjet(e.target.value)}
                                        className="flex-1 border-b border-gray-300 focus:border-blue-500 outline-none text-sm text-gray-800 pb-0.5 bg-transparent"
                                        placeholder="Objet du document..."
                                        data-testid="editor-objet"
                                    />
                                </div>
                            </div>

                            {/* Body - editable document text */}
                            <div className="px-10 py-6">
                                <textarea
                                    value={editorBody}
                                    onChange={(e) => setEditorBody(e.target.value)}
                                    className="w-full min-h-[500px] text-sm text-gray-800 leading-relaxed resize-none outline-none bg-transparent"
                                    style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '13px', lineHeight: '1.8' }}
                                    placeholder={editorType ? "Redigez le contenu du document ici..." : "Selectionnez d'abord un type de document dans la barre d'outils ci-dessus."}
                                    data-testid="editor-body"
                                />
                            </div>

                            {/* Signature area */}
                            <div className="px-10 pb-10 text-right">
                                <div className="inline-block text-left border-t border-gray-300 pt-3 min-w-[200px]">
                                    <p className="text-xs font-bold text-gray-700">{communeName ? `Le Maire de ${communeName}` : 'Le Maire'}</p>
                                    <div className="h-16"></div>
                                    <p className="text-[10px] text-gray-400 italic">Signature</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Commune Settings */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent className="max-w-sm bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle>Parametres commune</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Nom de la commune</Label>
                            <Input value={communeInput} onChange={(e) => setCommuneInput(e.target.value)} className="tactical-input" placeholder="Ex: Marseille" data-testid="commune-name-input" />
                            <p className="text-xs text-zinc-500 mt-1">Pre-remplit automatiquement l'en-tete de tous les documents</p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Annuler</Button>
                            <Button onClick={handleSaveCommune} className="tactical-btn" data-testid="save-commune">Enregistrer</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DocumentsPage;
